import React, { useCallback, useEffect, useRef } from 'react';
import { Routes, Route, Link, useLocation } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { useAuth } from './contexts/AuthContext';

import { Navigation } from './components/Navigation';
import { RequireAuthRoute } from './components/RequireAuthRoute';
import { RequireFirmRoute } from './components/RequireFirmRoute';

import { SummaryPage } from './pages/SummaryPage';
import { FilingsPage } from './pages/FilingsPage';
import { SummariesPage } from './pages/SummariesPage';
import { BookmarksPage } from './pages/BookmarksPage';

import { HomePage } from './pages/HomePage';
import { AccountPage } from './pages/AccountPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { PricingPage } from './pages/PricingPage';
import { AdminEnterprisePage } from './pages/AdminEnterprisePage';
import { TeamsPage } from './pages/TeamsPage';
import { WorkspacePage } from './pages/WorkspacePage';
import { WorkspacesPage } from './pages/WorkspacesPage';
import { ProceedingsPage } from './pages/ProceedingsPage';
import { ProceedingDetailsPage } from './pages/ProceedingDetailsPage';
import { ReplyWorkbenchPage } from './pages/ReplyWorkbenchPage';
import { FirmExParteLandingPage } from './pages/FirmExParteLandingPage';
import { ProceedingDigestPage } from './pages/ProceedingDigestPage';
import { ProceedingTopicPage } from './pages/ProceedingTopicPage';
import { SkipLink, A11yLiveRegions, RouteFocus, MAIN_CONTENT_ID } from './lib/a11y';
import { CommentSummariesEntryPage } from './pages/CommentSummariesEntryPage';
import { CommentSummariesPage } from './pages/CommentSummariesPage';
import { TrendingDocketsPage } from './pages/TrendingDocketsPage';
import { WatchlistPage } from './pages/WatchlistPage';
import { FirmConsolePage } from './pages/FirmConsolePage';
import { DocsPage } from './pages/DocsPage';
import { ReleaseNotesPage } from './pages/ReleaseNotesPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { SupportPage } from './pages/SupportPage';
import { StatusPage } from './pages/StatusPage';
import { IngestDashboardPage } from './pages/IngestDashboardPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { PublicFilesPage } from './pages/PublicFilesPage';
import { SharedSummaryPage } from './pages/SharedSummaryPage';

// Error boundary component with Sentry integration
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorDetails = {
      error: error,
      message: error.message,
      stack: error.stack,
      errorInfo: errorInfo,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    console.error('🚨 React Error Boundary caught an error:', errorDetails);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', color: 'white', backgroundColor: '#1a1a1a' }}>
          <h1>Something went wrong.</h1>
          <p>Error: {this.state.error?.message}</p>
          <button onClick={() => window.location.reload()}>Reload Page</button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Best-effort referral attribution after a new magic-link sign-in.
// Uses the ?ref= query param captured from the initial outreach/landing URL.
const processReferralIfPresent = async (userId: string) => {
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const referrerUserId = urlParams.get('ref');
    if (!referrerUserId) return;

    const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/process-referral`;
    await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        referrerUserId,
        newUserId: userId,
      }),
    });
  } catch (referralError) {
    console.error('Error processing referral:', referralError);
  }
};

function App() {
  const location = useLocation();
  const { user, loading: authLoading, isAnonymous, openAuthModal, signOut } = useAuth();

  // The landing/nav auth CTAs ("Sign Up Free", "Log In") are unified magic-link
  // entry points. Send them to /onboarding: Supabase stamps #type=signup for a
  // brand-new account (→ OnboardingPage shows the follow picker) vs #type=magiclink
  // for a returning user (→ OnboardingPage bounces home). Without an explicit
  // redirect, openAuthModal defaults to the current page, so a first-time signup
  // landed on "/" and never saw onboarding. requireAuth flows (Follow, generate)
  // open the modal separately with a current-page redirect, so they're unaffected.
  const openSignupModal = useCallback(() => {
    openAuthModal({
      emailRedirectTo:
        typeof window !== 'undefined' ? `${window.location.origin}/onboarding` : undefined,
      headline: 'Sign in or create your account',
      subhead:
        "Enter your email and we'll send you a magic link — no password required. New here? You'll pick a few dockets to follow next.",
    });
  }, [openAuthModal]);

  // ?signup=true in the URL opens the auth modal — handy for "Sign up"
  // buttons that don't have their own click handler (marketing CTAs).
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('signup') === 'true') {
      openSignupModal();
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [openSignupModal]);

  // Referral attribution runs once per sign-in. The auth provider owns
  // the listener now, so we watch the user transition (null → object)
  // here. The edge function is idempotent so duplicate fires are safe,
  // but we still gate with a ref to avoid re-running on every re-render.
  const lastReferralUserIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (user?.id && lastReferralUserIdRef.current !== user.id) {
      lastReferralUserIdRef.current = user.id;
      processReferralIfPresent(user.id);
    }
  }, [user]);

  const handleSignOut = signOut;
  const handleShowAuth = openSignupModal;

  if (authLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-cg-cream bg-[var(--cg-cream)]"
        style={{ backgroundColor: 'var(--cg-cream)' }}
      >
        <RefreshCw className="h-8 w-8 animate-spin text-cg-green" />
      </div>
    );
  }

  const isDocsRoute =
    location.pathname === '/docs' ||
    location.pathname === '/docs/releases' ||
    location.pathname === '/feedback' ||
    location.pathname === '/support';

  // /s/:token is a public, shareable summary view. It renders its own
  // slim header + footer and should not be wrapped in the in-app
  // Navigation chrome (which produced a duplicate header for share
  // recipients).
  const isSharedSummaryRoute = location.pathname.startsWith('/s/');
  // /d/:firm/:docket is a public, firm-branded ex-parte landing page (the digest
  // "view all" target) — renders its own branded header, no in-app chrome.
  const isDigestLandingRoute = location.pathname.startsWith('/d/');
  const isChromelessRoute = isDocsRoute || isSharedSummaryRoute || isDigestLandingRoute;
  const isFirmRoute = location.pathname.startsWith('/firm');

  // Proceeding detail AND digest pages render their own full-bleed gradient
  // header + self-centered content, so they must NOT be wrapped in the padded,
  // max-w-7xl, cream-background <main> (which framed them in an odd boxed border).
  const isProceedingDetailRoute =
    /^\/proceedings\/[^/]+$/.test(location.pathname) ||
    /^\/proceedings\/[^/]+\/digest(\/[^/]+)?$/.test(location.pathname) ||
    /^\/proceedings\/digest\/[^/]+$/.test(location.pathname);

  return (
    <ErrorBoundary>
      <SkipLink />
      <A11yLiveRegions />
      <RouteFocus />
      <div
        className={`relative min-h-screen ${isChromelessRoute ? 'bg-white' : 'bg-cg-cream bg-[var(--cg-cream)]'}`}
        style={isChromelessRoute ? undefined : { backgroundColor: 'var(--cg-cream)' }}
      >
        {!isChromelessRoute && (
          <Navigation
            userEmail={user?.email || null}
            onSignOut={handleSignOut}
            isAnonymous={isAnonymous}
            onShowAuth={handleShowAuth}
          />
        )}

        <div className="relative z-10 flex min-h-screen flex-col overflow-x-hidden">
          <main
            id={MAIN_CONTENT_ID}
            tabIndex={-1}
            className={
              `outline-none ` +
              (isChromelessRoute || isProceedingDetailRoute || isFirmRoute
                ? 'w-full flex-1'
                : 'mx-auto w-full max-w-7xl flex-1 px-4 py-4 sm:px-8 sm:py-8')
            }
          >
            {/* Auth modal is rendered by AuthProvider — see src/contexts/AuthContext.tsx */}

            <Routes>
              <Route
                path="/"
                element={
                  <HomePage
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    userEmail={user?.email || null}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route
                path="/home"
                element={
                  <HomePage
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    userEmail={user?.email || null}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route
                path="/business"
                element={
                  <HomePage
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    userEmail={user?.email || null}
                    onSignOut={handleSignOut}
                    audience="business"
                  />
                }
              />
              <Route path="/d/:firmSlug/:docketNumber" element={<FirmExParteLandingPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/bookmarks" element={<SummaryPage />} />

              <Route path="/library" element={<SummaryPage />} />
              <Route path="/filings/:docketNumber" element={<FilingsPage />} />
              <Route path="/summaries/:docketNumber" element={<SummariesPage />} />
              <Route
                path="/account"
                element={
                  <RequireAuthRoute intentLabel="view your account">
                    <AccountPage />
                  </RequireAuthRoute>
                }
              />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route
                path="/docs"
                element={
                  <DocsPage
                    userEmail={user?.email || (isAnonymous ? 'Guest User' : null)}
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route
                path="/docs/releases"
                element={
                  <ReleaseNotesPage
                    userEmail={user?.email || (isAnonymous ? 'Guest User' : null)}
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route
                path="/feedback"
                element={
                  <FeedbackPage
                    userEmail={user?.email || (isAnonymous ? 'Guest User' : null)}
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route
                path="/support"
                element={
                  <SupportPage
                    userEmail={user?.email || (isAnonymous ? 'Guest User' : null)}
                    isAnonymous={isAnonymous}
                    onSignIn={handleShowAuth}
                    onSignOut={handleSignOut}
                  />
                }
              />
              <Route path="/status" element={<StatusPage />} />
              <Route
                path="/admin/enterprise"
                element={
                  <RequireAuthRoute intentLabel="access the admin console">
                    <AdminEnterprisePage />
                  </RequireAuthRoute>
                }
              />
              <Route
                path="/workspaces"
                element={
                  <RequireAuthRoute intentLabel="view your workspaces">
                    <WorkspacesPage />
                  </RequireAuthRoute>
                }
              />
              <Route
                path="/teams"
                element={
                  <RequireAuthRoute intentLabel="view your teams">
                    <TeamsPage />
                  </RequireAuthRoute>
                }
              />
              <Route
                path="/workspace/:workspaceId"
                element={
                  <RequireAuthRoute intentLabel="open this workspace">
                    <WorkspacePage />
                  </RequireAuthRoute>
                }
              />
              <Route path="/proceedings" element={<ProceedingsPage />} />
              <Route path="/proceedings/:docketNumber" element={<ProceedingDetailsPage />} />
              <Route path="/proceedings/:docketNumber/reply" element={<ReplyWorkbenchPage />} />
              <Route path="/proceedings/:docketNumber/digest" element={<ProceedingDigestPage />} />
              <Route path="/proceedings/:docketNumber/digest/:topicId" element={<ProceedingTopicPage />} />
              {/* alias: digest/{docket} order also resolves to the digest page */}
              <Route path="/proceedings/digest/:docketNumber" element={<ProceedingDigestPage />} />
              <Route path="/comment-summaries" element={<CommentSummariesEntryPage />} />
              <Route path="/comment-summaries/:docketNumber" element={<CommentSummariesPage />} />
              <Route path="/s/:token" element={<SharedSummaryPage />} />
              <Route path="/trending" element={<TrendingDocketsPage />} />
              <Route
                path="/watchlist"
                element={
                  <RequireAuthRoute intentLabel="view your watchlist">
                    <WatchlistPage />
                  </RequireAuthRoute>
                }
              />
              <Route
                path="/firm"
                element={
                  <RequireFirmRoute>
                    <FirmConsolePage />
                  </RequireFirmRoute>
                }
              />
              <Route
                path="/ingest-dashboard"
                element={
                  <RequireAuthRoute intentLabel="view the ingest dashboard">
                    <IngestDashboardPage userEmail={user?.email || null} />
                  </RequireAuthRoute>
                }
              />
              <Route
                path="/public-files"
                element={
                  <RequireAuthRoute intentLabel="view public files">
                    <PublicFilesPage userEmail={user?.email || null} />
                  </RequireAuthRoute>
                }
              />
              <Route
                path="*"
                element={
                  <div className="mx-auto max-w-md py-32 text-center">
                    <h1 className="mb-2 text-2xl font-bold text-gray-900">Page not found</h1>
                    <p className="mb-6 text-sm text-gray-500">
                      That page doesn't exist or may have moved.
                    </p>
                    <Link
                      to="/proceedings"
                      className="inline-block rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700"
                    >
                      Browse proceedings
                    </Link>
                  </div>
                }
              />
            </Routes>
          </main>

          {!isChromelessRoute && (
            <footer
              className="mt-auto flex flex-col gap-4 border-t border-neutral-200 bg-cg-nav bg-[var(--cg-nav)] px-4 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-9"
              style={{ backgroundColor: 'var(--cg-nav)' }}
            >
              <span className="text-xs font-light text-white/30">
                © 2026 CommsGenie. All rights reserved.
              </span>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-light">
                <Link to="/privacy" className="text-white/35 hover:text-white/70 transition-colors">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="text-white/35 hover:text-white/70 transition-colors">
                  Terms of Use
                </Link>
                <Link to="/docs" className="text-white/35 hover:text-white/70 transition-colors">
                  Documentation
                </Link>
                <Link to="/status" className="text-white/35 hover:text-white/70 transition-colors">
                  Status
                </Link>
              </div>
            </footer>
          )}
        </div>
      </div>
    </ErrorBoundary>
  );
}

export default App;
